import { useEffect, useRef, useCallback, useMemo, useState } from "react";
import { CheckCircle2, RotateCcw } from "lucide-react";

import type { FormQuestion, PublicFormVersion } from "@kushya/shared";
import { useRespondentConversation } from "@/hooks/useRespondentConversation";
import { ThemeProvider } from "@/components/ThemeProvider";
import { Button } from "@/components/ui/button";
import { FormattedCopyMarkdown } from "@/components/form-copy/FormattedCopyMarkdown";
import {
  AnswerSummary,
  ChatMessages,
  ChatInput,
  TypingIndicator,
  ConversationHeader,
  ConversationWelcome,
  ConversationFooter,
} from "@/components/chat";
import {
  QuickOptions,
  InlineFileUpload,
  InlineSignaturePad,
} from "@/components/chat/inline";

type ChoiceQuestion = Extract<
  FormQuestion,
  { type: "multiple_choice_single" | "multiple_choice_multi" }
>;

type FileUploadQuestion = Extract<FormQuestion, { type: "file_upload" }>;

/**
 * Type guard to check if a question has options (multiple choice)
 */
function isChoiceQuestion(q: FormQuestion): q is ChoiceQuestion {
  return (
    q.type === "multiple_choice_single" || q.type === "multiple_choice_multi"
  );
}

/**
 * Type guard to check if a question is file upload
 */
function isFileUploadQuestion(q: FormQuestion): q is FileUploadQuestion {
  return q.type === "file_upload";
}

interface ConversationalScreenProps {
  projectId: string;
  formId: string;
  version: number;
  formVersion: PublicFormVersion;
}

/**
 * Main container for the conversational form experience.
 * Shows welcome screen, orchestrates chat, and handles completion.
 */
export function ConversationalScreen({
  projectId,
  formId,
  version,
  formVersion,
}: ConversationalScreenProps) {
  const [hasStarted, setHasStarted] = useState(false);

  const {
    transcript,
    isStreaming,
    streamingText,
    sendMessage,
    startConversation,
    resetConversation,
    currentComponent,
    shouldRenderComponent,
    progress,
    isComplete,
    currentQuestionRef,
    answers,
    error,
    dismissError,
    isRestoringSession,
    sessionToken,
    responseId,
  } = useRespondentConversation(projectId, formId, version);

  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Auto-skip welcome screen when restoring a valid session
  useEffect(() => {
    if (hasStarted) {
      return;
    }

    if (isRestoringSession) {
      return;
    }

    if (sessionToken && responseId) {
      setHasStarted(true);
    }
  }, [hasStarted, isRestoringSession, responseId, sessionToken]);

  // Auto-scroll to bottom on new messages or streaming
  useEffect(() => {
    if (hasStarted) {
      messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }
  }, [transcript, streamingText, isStreaming, hasStarted]);

  // Get current question for inline components
  const currentQuestion = currentQuestionRef
    ? formVersion.questions.find((q) => q.ref === currentQuestionRef)
    : null;

  // Get options if current question is a choice question
  const currentOptions = useMemo(() => {
    if (currentQuestion && isChoiceQuestion(currentQuestion)) {
      return currentQuestion.options ?? [];
    }
    return [];
  }, [currentQuestion]);

  // Get file upload config if current question is file upload
  const currentFileConfig = useMemo(() => {
    if (currentQuestion && isFileUploadQuestion(currentQuestion)) {
      return {
        maxFiles: currentQuestion.maxFiles,
        maxSizeBytes: (currentQuestion.maxSizeMb ?? 10) * 1024 * 1024,
        allowedTypes: currentQuestion.accept,
      };
    }
    return undefined;
  }, [currentQuestion]);

  // Handle starting the conversation
  const handleStart = useCallback(async () => {
    setHasStarted(true);
    await startConversation();
  }, [startConversation]);

  // Handle restarting
  const handleRestart = useCallback(() => {
    setHasStarted(false);
    resetConversation();
  }, [resetConversation]);

  // Handle option selection for multiple choice
  const handleOptionSelect = useCallback(
    (optionId: string) => {
      const option = currentOptions.find((o) => o.id === optionId);
      if (option) {
        void sendMessage(option.label);
      }
    },
    [currentOptions, sendMessage],
  );

  // Handle multi-select confirmation
  const handleMultiSelect = useCallback(
    (optionIds: string[]) => {
      const labels = optionIds
        .map((id) => currentOptions.find((o) => o.id === id)?.label)
        .filter(Boolean)
        .join(", ");
      if (labels) {
        void sendMessage(labels);
      }
    },
    [currentOptions, sendMessage],
  );

  // Handle file upload completion - send full metadata as JSON for backend storage
  const handleFileUpload = useCallback(
    (
      files: Array<{
        fileKey: string;
        fileName: string;
        fileSize: number;
        mimeType: string;
        url: string;
      }>,
    ) => {
      // For single file, send the object directly; for multiple, send array
      const payload = files.length === 1 ? files[0] : files;
      void sendMessage(JSON.stringify(payload));
    },
    [sendMessage],
  );

  // Handle signature submission - send the URL for backend extraction
  const handleSignature = useCallback(
    (signatureUrl: string) => {
      void sendMessage(signatureUrl);
    },
    [sendMessage],
  );

  const theme = formVersion.ui?.theme;
  const logoUrl = theme?.logoUrl;
  const introText = formVersion.ui?.copy?.introduction;
  const thankYouMessage =
    formVersion.ui?.copy?.thankYou || "Thank you for completing the form!";
  const totalQuestions = formVersion.questions.length;
  const estimatedMinutes = formVersion.ui?.copy?.estimatedMinutes ?? undefined;
  const showProgress = formVersion.ui?.layout?.showProgress !== false;

  // Loading state while checking for existing session
  if (isRestoringSession) {
    return (
      <ThemeProvider theme={theme}>
        <div className="flex min-h-screen items-center justify-center bg-background">
          <div className="text-center">
            <div className="mx-auto mb-4 size-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
            <p className="text-sm text-muted-foreground">Loading...</p>
          </div>
        </div>
      </ThemeProvider>
    );
  }

  // Welcome screen (before conversation starts)
  if (!hasStarted) {
    return (
      <ThemeProvider theme={theme}>
        <ConversationWelcome
          logoUrl={logoUrl}
          introText={introText}
          totalQuestions={totalQuestions}
          estimatedMinutes={estimatedMinutes}
          onStart={handleStart}
          isStarting={isStreaming}
        />
      </ThemeProvider>
    );
  }

  // Completion screen
  if (isComplete) {
    return (
      <ThemeProvider theme={theme}>
        <div className="flex min-h-screen flex-col bg-gradient-to-b from-background to-muted/30">
          <ConversationHeader
            progress={100}
            logoUrl={logoUrl}
            showProgress={showProgress}
          />

          <main className="flex flex-1 flex-col items-center justify-center px-4 py-12">
            <div className="w-full max-w-md space-y-8 text-center">
              {/* Success icon */}
              <div className="flex justify-center">
                <div className="flex size-20 items-center justify-center rounded-full bg-green-100 dark:bg-green-900/30">
                  <CheckCircle2 className="size-10 text-green-600 dark:text-green-400" />
                </div>
              </div>

              {/* Message */}
              <div className="space-y-3">
                <h1 className="text-2xl font-semibold tracking-tight text-foreground">
                  All done!
                </h1>
                <FormattedCopyMarkdown
                  content={thankYouMessage}
                  variant="conversation"
                  className="text-center"
                />
              </div>

              {/* Your responses */}
              {answers && Object.keys(answers).length > 0 && (
                <div className="rounded-xl border border-border bg-card p-6 text-left shadow-sm">
                  <h2 className="mb-4 text-sm font-medium text-muted-foreground">
                    Your Responses
                  </h2>
                  <div className="max-h-64 overflow-y-auto">
                    <AnswerSummary
                      questions={formVersion.questions}
                      answers={answers}
                    />
                  </div>
                </div>
              )}

              {/* Restart button */}
              <Button
                variant="outline"
                onClick={handleRestart}
                className="gap-2"
              >
                <RotateCcw className="size-4" />
                Start new conversation
              </Button>
            </div>
          </main>

          <ConversationFooter showHints={false} />
        </div>
      </ThemeProvider>
    );
  }

  // Active conversation
  return (
    <ThemeProvider theme={theme}>
      <div className="flex min-h-screen flex-col bg-background">
        <ConversationHeader
          progress={progress}
          logoUrl={logoUrl}
          showProgress={showProgress}
        />

        <main className="flex flex-1 flex-col">
          {/* Messages area */}
          <div className="flex-1 overflow-y-auto">
            <div className="mx-auto w-full max-w-lg px-4 py-6">
              <ChatMessages
                messages={transcript}
                streamingText={streamingText}
              />

              {isStreaming && !streamingText && <TypingIndicator />}

              {/* Inline components */}
              {shouldRenderComponent && currentQuestion && !isStreaming && (
                <div className="mt-6 animate-in fade-in-0 slide-in-from-bottom-2 duration-300">
                  {currentComponent === "select" && (
                    <QuickOptions
                      options={currentOptions}
                      multiSelect={
                        currentQuestion.type === "multiple_choice_multi"
                      }
                      onSelect={handleOptionSelect}
                      onMultiSelect={handleMultiSelect}
                    />
                  )}

                  {currentComponent === "upload" && (
                    <InlineFileUpload
                      questionRef={currentQuestion.ref}
                      config={currentFileConfig}
                      onUpload={handleFileUpload}
                      sessionToken={sessionToken ?? undefined}
                    />
                  )}

                  {currentComponent === "signature_pad" && (
                    <InlineSignaturePad
                      onSign={handleSignature}
                      sessionToken={sessionToken ?? undefined}
                    />
                  )}
                </div>
              )}

              <div ref={messagesEndRef} />
            </div>
          </div>

          {/* Error display */}
          {error && (
            <div className="border-t border-destructive/20 bg-destructive/5 px-4 py-3">
              <div className="mx-auto flex max-w-lg items-center justify-between gap-4">
                <p className="text-sm text-destructive">{error}</p>
                <button
                  type="button"
                  onClick={dismissError}
                  className="shrink-0 text-sm font-medium text-destructive hover:underline"
                >
                  Dismiss
                </button>
              </div>
            </div>
          )}

          {/* Input area */}
          <div className="border-t border-border/50 bg-background/95 px-4 py-4 backdrop-blur-sm supports-[backdrop-filter]:bg-background/80">
            <div className="mx-auto w-full max-w-lg">
              <ChatInput
                onSend={sendMessage}
                disabled={isStreaming || shouldRenderComponent}
                placeholder={
                  shouldRenderComponent
                    ? "Please use the options above"
                    : "Type your answer..."
                }
              />
            </div>
          </div>

          <ConversationFooter />
        </main>
      </div>
    </ThemeProvider>
  );
}
